import { DateInput } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import PeriodSelect from './PeriodSelect';

import DateRangeFields from '@/components/DateRangeFields/DateRangeFields';
import { usePeriodOptions, usePeriodParam } from '@/hooks/objectives/usePeriodOptions';
import { parseIsoDate, toDateInputLang, toIsoDate } from '@/utils/dateUtils';

/**
 * The period lens of the objectives screens: the cycle picker, plus -- once "custom dates" is
 * picked -- the two bounds of a range of the reader's own.
 *
 * It holds no state: the lens lives in the address (`?period=`, `?from=`, `?to=`), which is where
 * the lists and the pillar totals read it too, so the control and what it filters can never
 * disagree. That is what lets the employee portal mount it beside its tabs while the tab below
 * reads the same lens without a prop passing between them.
 */
export default function ObjectivePeriodFilter() {
    const { t, i18n } = useTranslation();
    const { options, defaultPeriodId, isLoading } = usePeriodOptions();
    const { periodId, setPeriodId, isCustomDates, customRange, setCustomRange } = usePeriodParam(
        options,
        defaultPeriodId,
        { allowCustomDates: true },
    );

    const lang = toDateInputLang(i18n.language);
    const startDateLabel = t('objectives.form.startDate');
    const endDateLabel = t('objectives.form.endDate');
    const startDate = parseIsoDate(customRange.from);
    const endDate = parseIsoDate(customRange.to);

    return (
        <div
            className={
                'flex flex-wrap items-center gap-3 max-sm:w-full max-sm:flex-col max-sm:items-stretch'
            }
        >
            <PeriodSelect
                options={options}
                value={periodId}
                onChange={setPeriodId}
                allowCustomDates
                isLoading={isLoading}
            />

            {isCustomDates && (
                <DateRangeFields
                    dataTestId={'objectives-period-range'}
                    startField={
                        <DateInput
                            value={customRange.from}
                            onChange={(date) =>
                                setCustomRange({
                                    ...customRange,
                                    from: date ? toIsoDate(date) : '',
                                })
                            }
                            // A range that ends before it starts narrows to nothing and reads as
                            // "you have no objective": neither bound is offered past the other.
                            disabledDates={endDate ? { after: endDate } : undefined}
                            lang={lang}
                            placeholder={startDateLabel}
                            ariaLabel={startDateLabel}
                            // The toolbars sit on a glass surface and open a stacking context of
                            // their own; rendered in the flow, the calendar would be trapped under
                            // the page.
                            portalPanel
                            dataTestId={'objectives-period-from'}
                        />
                    }
                    endField={
                        <DateInput
                            value={customRange.to}
                            onChange={(date) =>
                                setCustomRange({ ...customRange, to: date ? toIsoDate(date) : '' })
                            }
                            disabledDates={startDate ? { before: startDate } : undefined}
                            lang={lang}
                            placeholder={endDateLabel}
                            ariaLabel={endDateLabel}
                            portalPanel
                            dataTestId={'objectives-period-to'}
                        />
                    }
                />
            )}
        </div>
    );
}
